import { __awaiter } from "tslib";
import React, { Fragment } from 'react';
import { HorizontalWrapper } from 'moet-core';
import { ButtonQuaternary, SpacerVertical, SpacerHorizontal } from '@klarna/bubble-ui';
import { ContainerTemplate, CallToActionButton, SecondaryButton } from '@/components';
import { OnboardingFieldSetComponent } from '@/bpc/components/OnboardingFieldComponent/OnboardingFieldSetComponent';
import { WithTermsAndConditions, } from '@/bpc/components/HOCs/WithTermsAndConditions';
import { makeSubtitleElement } from '@/bpc/components/OnboardingGroupComponent/messageRenderer';
import { ANALYTICS_EVENTS } from '@/bpc/utils/analyticsEvents';
class _OnboardingGroupComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            highlightMandatoryButEmptyFields: false,
            ctaClicked: false,
            isLoading: false,
            errorOnSubmit: null,
        };
        this.onAddItem = this.onAddItem.bind(this);
        this.onDeleteItem = this.onDeleteItem.bind(this);
        this.onBlurOfField = this.onBlurOfField.bind(this);
        this.handleOnBack = this.handleOnBack.bind(this);
        this.handleOnNext = this.handleOnNext.bind(this);
        this.navigateToEntryPoint = this.navigateToEntryPoint.bind(this);
        this.handleOnSubmit = this.handleOnSubmit.bind(this);
        this.callToActionOnNextWrapper = this.callToActionOnNextWrapper.bind(this);
    }
    onAddItem(e) {
        e.preventDefault();
        this.props.group.addItem();
        this.forceUpdate();
    }
    onBlurOfField(field) {
        return (value) => {
            this.props.group.setValueOfField(field, value);
            // to recalculate whether to disable Next button
            this.forceUpdate();
        };
    }
    onDeleteItem(index) {
        this.props.group.deleteItem(index);
        this.forceUpdate();
    }
    callToActionOnNextWrapper(group, onNextFn) {
        const { missingTermsValidation } = this.props;
        if (!group.allMandatoryFilled() || missingTermsValidation) {
            this.setState({ ctaClicked: true, highlightMandatoryButEmptyFields: true });
            return;
        }
        this.setState({ highlightMandatoryButEmptyFields: false });
        return onNextFn();
    }
    navigateToEntryPoint() {
        const { entryPoint, onboarding } = this.props;
        if (entryPoint) {
            onboarding.logEvent(ANALYTICS_EVENTS.entryPointClicked, { entryPoint });
            window.location.href = entryPoint;
        }
    }
    handleOnBack() {
        const { hasPreviousGroup, onPreviousGroup } = this.props;
        if (hasPreviousGroup) {
            onPreviousGroup();
        }
        else {
            this.navigateToEntryPoint();
        }
    }
    handleOnSubmit() {
        return __awaiter(this, void 0, void 0, function* () {
            const { onSubmit, onboarding, onSubmitTermsAndConditionsFn, missingTermsValidation, hasTermsToAgree, } = this.props;
            // Early return - missing accept terms OR has already accepted terms
            if (missingTermsValidation) {
                this.setState({ ctaClicked: true, errorOnSubmit: null });
                return;
            }
            this.setState({ isLoading: true, errorOnSubmit: null });
            try {
                const submitTermsPromise = hasTermsToAgree ? onSubmitTermsAndConditionsFn : Promise.resolve;
                yield submitTermsPromise();
                yield onboarding.pushPayloadToBackendAndTriggerContinueEvent(this.props.group.groupId);
                yield onboarding.submitApplication();
                this.setState({ isLoading: false });
                yield onSubmit();
            }
            catch (errorOnSubmit) {
                this.setState({ isLoading: false, errorOnSubmit });
                // Scroll to the top
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
        });
    }
    handleOnNext() {
        const { group, hasNextGroup, onNextGroup, onShowSummaryPage, hasTermsToAgree, shouldSkipSummaryPage, } = this.props;
        const { callToActionOnNextWrapper } = this;
        if (hasNextGroup) {
            return callToActionOnNextWrapper(group, onNextGroup);
        }
        if (hasTermsToAgree && shouldSkipSummaryPage) {
            return callToActionOnNextWrapper(group, this.handleOnSubmit);
        }
        return callToActionOnNextWrapper(group, onShowSummaryPage);
    }
    render() {
        const { group, error, hasPreviousGroup, t, TermsAndConditionsComponent, shouldSkipSummaryPage, entryPoint, } = this.props;
        const translationPrefix = 'products-fe.general.buttons.';
        const nextButtonTranslationKey = `${translationPrefix}${shouldSkipSummaryPage ? 'submit_application' : 'continue'}`;
        const { ctaClicked, isLoading, errorOnSubmit } = this.state;
        const showBackButton = hasPreviousGroup || !!entryPoint;
        return (React.createElement(ContainerTemplate, { title: t(group.labelTitle) || group.fallbackTitle, subtitle: makeSubtitleElement(group) || group.fallbackSubtitle, error: error || errorOnSubmit },
            group.canHaveMultipleItems() ? (React.createElement(React.Fragment, null,
                group.fields.map((fields, i) => (
                // TODO don't use index as key prop
                // https://robinpokorny.com/blog/index-as-a-key-is-an-anti-pattern/
                React.createElement(Fragment, { key: `item-${i}` },
                    React.createElement(OnboardingFieldSetComponent, { fields: fields, getValueForField: group.getValueForField, setValueOfField: group.setValueOfField, onBlurField: this.onBlurOfField, onDelete: group.canDeleteItem() ? () => this.onDeleteItem(i) : undefined, t: t, highlightMandatoryButEmptyFields: this.state.highlightMandatoryButEmptyFields }),
                    React.createElement(SpacerVertical, { small: true })))),
                group.canAddAdditionalItem() && (React.createElement(React.Fragment, null,
                    React.createElement("div", { style: {
                            flex: 0,
                            display: 'flex',
                            justifyContent: 'flex-start',
                        } },
                        React.createElement(ButtonQuaternary, { size: "small", onClick: this.onAddItem }, t(`${translationPrefix}add_aditional_item`))),
                    React.createElement(SpacerVertical, { small: true }))))) : (React.createElement(OnboardingFieldSetComponent, { fields: group.fields[0], getValueForField: group.getValueForField, setValueOfField: group.setValueOfField, onBlurField: this.onBlurOfField, highlightMandatoryButEmptyFields: this.state.highlightMandatoryButEmptyFields, t: t })),
            React.createElement(TermsAndConditionsComponent, { isLoading: isLoading, ctaClicked: ctaClicked }),
            React.createElement(HorizontalWrapper, { display: "inline-flex", alignItems: "center", justifyContent: "flex-end" },
                showBackButton ? (React.createElement(React.Fragment, null,
                    React.createElement(SecondaryButton, { onClick: this.handleOnBack }, t(`${translationPrefix}back`)),
                    React.createElement(SpacerHorizontal, { medium: true }))) : null,
                React.createElement(CallToActionButton, { onClick: this.handleOnNext, loading: isLoading, disabled: isLoading }, t(nextButtonTranslationKey)))));
    }
}
export const OnboardingGroupComponent = WithTermsAndConditions(_OnboardingGroupComponent);
